import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { CurrentUser, LoginInput, RegisterInput } from '@talkvibe/shared';

import { setCsrfToken, setUnauthorizedHandler } from '../services/api.ts';
import { authService } from '../services/auth.ts';
import { browserLanguage, setLocale, storedLocale } from '../i18n/index.ts';

/**
 * Who is signed in.
 *
 * The session itself lives in an HttpOnly cookie the browser manages; this
 * store only mirrors it so the UI can render. `ready` distinguishes "we have
 * not checked yet" from "checked, and nobody is signed in" - without it the
 * router guard would bounce a signed-in user to /login on every reload.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);
  const ready = ref(false);
  const pending = ref(false);

  const isAuthenticated = computed(() => user.value !== null);

  /** Runs once at startup; later calls reuse the result. */
  let restorePromise: Promise<void> | null = null;

  /**
   * Sets the signed-in user and the interface language together.
   *
   * `users.language` means one thing: the language you read in. It decides
   * what messages are translated into and what the interface is shown in, so
   * they are set from the same place - a second setter would eventually be
   * added on one path and forgotten on another.
   *
   * Signing out falls back to the browser's language rather than to English:
   * the sign-in page is the first thing a Thai speaker sees, and it should
   * not be the one screen in the product that is not in their language.
   */
  function applyUser(next: CurrentUser | null): void {
    user.value = next;
    // The account's own setting wins: it was saved deliberately and follows
    // the person between devices. Only once there is no account does a choice
    // made in the header, and then the browser, get a say.
    setLocale(next?.language ?? storedLocale() ?? browserLanguage());
  }

  async function restore(): Promise<void> {
    if (ready.value) return;
    restorePromise ??= (async () => {
      const session = await authService.restoreSession();
      applyUser(session?.user ?? null);
      ready.value = true;
    })();
    return restorePromise;
  }

  async function login(input: LoginInput): Promise<void> {
    pending.value = true;
    try {
      const session = await authService.login(input);
      applyUser(session.user);
      ready.value = true;
    } finally {
      pending.value = false;
    }
  }

  /**
   * Starts a registration, which no longer ends in being signed in.
   *
   * The account does not exist until the link in the email is opened, so there
   * is nobody to apply. The address comes back for the page to say where to
   * look. The signed-in branch is for the minutes around a deploy, when a
   * browser running this code may still be talking to a server that creates the
   * account outright.
   */
  async function register(input: RegisterInput): Promise<{ pending: boolean; email: string }> {
    pending.value = true;
    try {
      const result = await authService.register(input);

      /**
       * Switch to the language they chose, even though nobody is signed in.
       *
       * It used to happen inside `applyUser`, which registration no longer
       * reaches — and the promise it keeps is the product's first one: choosing
       * Thai on the form and then being told to check your inbox in English is
       * that promise broken on the first screen. Everything from here until
       * they sign in — this panel, the mail, the verify page — is read by
       * somebody with no account to carry a preference.
       */
      setLocale(input.language);

      if (!result.pending) {
        applyUser(result.session.user);
        ready.value = true;
        return { pending: false, email: result.session.user.email };
      }
      return { pending: true, email: result.email };
    } finally {
      pending.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await authService.logout();
    } finally {
      clear();
    }
  }

  /** Drops local session state without calling the server. */
  function clear(): void {
    applyUser(null);
    ready.value = true;
    restorePromise = null;
    setCsrfToken(null);
  }

  /** Keeps the store in step after a profile edit. */
  function setUser(next: CurrentUser): void {
    applyUser(next);
  }

  /** Re-reads the account, e.g. after verifying an email address. */
  async function reload(): Promise<void> {
    try {
      user.value = await authService.me();
    } catch {
      // A failed refresh should not sign the user out; the next request will.
    }
  }

  // Any 401 from anywhere means the session is gone; reflect that immediately
  // rather than leaving the UI pretending the user is still signed in.
  setUnauthorizedHandler(clear);

  return {
    user,
    ready,
    pending,
    isAuthenticated,
    restore,
    login,
    register,
    logout,
    clear,
    setUser,
    reload,
  };
});
